import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Spin } from 'antd';
import { uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import Heading from 'LearningManager/components/Heading';
import styles from 'LearningManager/styles/CustomSetup.module.scss';
import CheckBoxCard from 'LearningManager/components/CheckBoxCard';
import { Icon, Input, Notification } from 'Components';
import classNames from 'classnames';
import InfinityScrollWrapper from 'Components/InfinityScroll/InfinityScrollWrapper';
import { useStepper } from 'LearningManager/hooks/Stepper.context';
import useAssignmentTenantId from 'LearningManager/hooks/useAssignmentTenantId';
import useAudienceTenantId from 'LearningManager/hooks/useAudienceTenantId';
import usePagedProperties from 'LearningManager/hooks/usePagedProperties';
import { Status } from 'app-types';
import { DirectoryGroup } from 'Services/Api/Directory/Types';
import { pluralLabel } from 'LearningManager/helpers/role';

const LIST_SCROLL_ID = 'lms-ca-au-property-scroll';

const toSelectedProperty = (property: DirectoryGroup) => ({
	id: property.id,
	name: property.name,
	label: property.label
});

const Property = observer(() => {
	const { tenantId, isTenantResolved } = useAudienceTenantId(
		useAssignmentTenantId()
	);
	const {
		pagedProperties,
		search,
		setSearch,
		isSearchPending
	} = usePagedProperties(tenantId, isTenantResolved);
	const { t } = useTranslation();
	const properties = pagedProperties.items;

	const {
		draft: {
			customSetup: { selectedProperties }
		},
		toggleAssignUsersSelection,
		setAssignUsersSelection
	} = useStepper();

	const hasMore =
		pagedProperties.hasMore &&
		pagedProperties.loadMoreStatus !== Status.Failure;

	useEffect(() => {
		const list = document.getElementById(LIST_SCROLL_ID);
		if (list) list.scrollTop = 0;
	}, [pagedProperties.search]);

	const handleCheckboxChange = (id: number | string, checked: boolean) => {
		const numId = Number(id);
		const property = properties.find((p) => p.id === numId);
		if (!property) return;
		if (checked !== selectedProperties.some((p) => p.id === numId)) {
			toggleAssignUsersSelection(
				'selectedProperties',
				toSelectedProperty(property)
			);
		}
	};

	const isAllSelected =
		!hasMore &&
		properties.length > 0 &&
		properties.every((p) => selectedProperties.some((sp) => sp.id === p.id));
	const isLoading = pagedProperties.status === Status.Pending || isSearchPending;
	const isSelectingAll = pagedProperties.loadAllStatus === Status.Pending;

	const handleSelectAll = async () => {
		if (isSelectingAll) return;
		if (isAllSelected) {
			const listedIds = new Set(properties.map((p) => p.id));
			setAssignUsersSelection(
				'selectedProperties',
				selectedProperties.filter((p) => !listedIds.has(p.id))
			);
			return;
		}

		let pool = properties;
		if (hasMore) {
			try {
				const allProperties = await pagedProperties.loadAll();
				if (!allProperties) return;
				pool = allProperties;
			} catch {
				Notification.error({
					description: t('learning.assignUsers.options.group.loadPropertiesError')
				});
				return;
			}
		}

		setAssignUsersSelection(
			'selectedProperties',
			uniqBy([...selectedProperties, ...pool.map(toSelectedProperty)], 'id')
		);
	};

	return (
		<div id="lms-ca-assign-users-property">
			<div
				className={classNames(styles.filters, styles['filters--flex_end'])}
			>
				{properties.length ? <Input.Search
					id="lms-ca-au-property-search"
					className={styles.search}
					placeholder={t('learning.assignUsers.options.group.search')}
					suffix={<Icon type="search" role="img" />}
					// Keeps a suffix rendered, so the input isn't remounted mid-typing.
					allowClear
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/> : null}
			</div>
			<div className={styles.header}>
				<Heading
					titleClassName={styles.title}
					title={t('learning.assignUsers.options.group.selectProperties')}
					astrix={true}
				/>
				{properties.length > 1 && (
					<div
						id="lms-ca-au-property-select-all"
						className={styles.selectAllBtn}
						onClick={handleSelectAll}
					>
						{isSelectingAll
							? t('learning.assignUsers.options.group.loadingProperties')
							: isAllSelected
							? t('learning.assignUsers.options.group.deselectAll')
							: t('learning.assignUsers.options.group.selectAll')}
					</div>
				)}
			</div>
			<InfinityScrollWrapper
				dataLength={properties.length}
				hasMore={hasMore && !isLoading}
				onLoadMore={() => pagedProperties.loadMore().catch(() => undefined)}
				loading={pagedProperties.loadMoreStatus === Status.Pending}
				scrollableId={LIST_SCROLL_ID}
				endMessageText=""
			>
			<div id="lms-ca-au-property-list" className={styles.container}>
				{isLoading && <div className={styles.loading}><Spin /></div>}
				{!isLoading &&
					!properties.length &&
					pagedProperties.status === Status.Success && (
						<div className={styles.empty}>
							{t('learning.assignUsers.options.group.noPropertiesFound')}
						</div>
					)}
				{!isLoading && pagedProperties.status === Status.Failure && (
					<div className={styles.empty}>
						{t('learning.assignUsers.options.group.loadPropertiesError')}
					</div>
				)}
				{properties.map((property) => {
					const isChecked = selectedProperties.some(
						(p) => p.id === property.id
					);
					return (
						<CheckBoxCard
							key={property.id}
							id={property.id}
							htmlId={`lms-ca-au-property-${property.id}`}
							icon={
								<Icon
									type={
										isChecked ? 'propOptFilled' : 'propOptIcon'
									}
								/>
							}
							title={property.name}
							desc={
								property.userCount === undefined
									? ''
									:  t(pluralLabel('learning.courseAssignments.bulkResetModal.users', property.userCount), { count: property.userCount })
							}
							isChecked={isChecked}
							onChange={handleCheckboxChange}
						/>
					);
				})}
			</div>
			</InfinityScrollWrapper>
		</div>
	);
});

export default Property;
